import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Link,
  Divider,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { auth } from '../../services/api';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';
import SEOHead from '../common/SEOHead';
import DevLoginBypass from './DevLoginBypass';
import { useSession } from '../../contexts/SessionContext';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { login } = useSession();

  // Handle ResizeObserver errors
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  useEffect(() => {
    // Check for Google OAuth callback
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const provider = params.get('provider');
    
    if (token && provider === 'google') {
      // Use SessionContext login method to immediately update authentication state
      login(token);
      navigate('/dashboard');
      return;
    }
    
    // Check for account lockout error in URL
    if (params.get('error') === 'account_locked') {
      setError('Your account has been temporarily locked due to too many failed login attempts. Please try again later.');
    } else if (params.get('error') === 'oauth_failed') {
      setError('Google sign-in failed. Please try again.');
    } else if (params.get('error') === 'token_error') {
      setError('Authentication failed. Please try again.');
    } else if (params.get('error') === 'callback_error') {
      setError('An error occurred during authentication. Please try again.');
    }
  }, [location, navigate, login]);

  const { email, password } = formData;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      console.log('Attempting login with email:', email);
      const { token } = await auth.login(email, password);
      console.log('Login successful, received token');
      
      // Use SessionContext login method to immediately update authentication state
      login(token);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err.response?.data || err.message);
      if (err.message === 'Too many login attempts. Please try again later.') {
        setError('Too many login attempts. Please try again after 15 minutes.');
      } else if (err.response?.status === 423) {
        setError('Your account has been temporarily locked. Please try again later.');
      } else {
        setError(err.response?.data?.msg || 'An error occurred during login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Use backend URL - dynamic based on environment
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? 'https://api.altififinance.com' 
      : 'http://localhost:5000';
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const features = [
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: '#FFA500' }} />,
      title: "Smart Budgeting",
      description: "Create and track budgets that adapt to your spending patterns"
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
      title: "Investment Tracking",
      description: "Monitor your portfolio performance and investment goals"
    },
    {
      icon: <InsightsIcon sx={{ fontSize: 40, color: '#FFA500' }} />,
      title: "Financial Insights",
      description: "Get personalized insights to optimize your financial health"
    }
  ];

  const loginStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Login - altifi",
    "description": "Sign in to your altifi account to access your personal finance dashboard. Manage your budget, track expenses, and monitor investments.",
    "url": "https://altifi.com/login",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="Login - altifi | Access Your Personal Finance Dashboard"
        description="Sign in to your altifi account to access your personal finance dashboard. Manage your budget, track expenses, and monitor investments securely."
        keywords="login, sign in, personal finance, budget login, altifi account, finance dashboard access"
        canonicalUrl="https://altifi.com/login"
        structuredData={loginStructuredData}
      />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #0F1410 0%, #1A1F1A 50%, #242924 100%)'
            : 'linear-gradient(135deg, #7C8B6F 0%, #A4B494 50%, #8B7C8B 100%)',
          margin: 0,
          padding: 0,
          overflow: { xs: 'auto', md: 'hidden' },
          position: 'relative'
        }}
      >
      {/* Marketing Section - Top on mobile, Left on desktop */}
      <Box
        sx={{
          flex: { xs: 'none', md: '0 0 65%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          py: { xs: 4, sm: 6, md: 0 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          minWidth: 0,
          minHeight: { xs: 'auto', md: '100vh' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.4)', // Dark overlay for better text contrast
            zIndex: 0,
          }
        }}
      >
        {/* Content */}
        <Box
          sx={{
            maxWidth: { xs: '100%', md: 600 },
            zIndex: 1,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'IBM Plex Sans, DM Sans, sans-serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                lineHeight: 1.2,
                textShadow: '0 3px 6px rgba(0,0,0,0.5)',
                letterSpacing: '-0.02em',
                textAlign: 'center',
                color: '#ffffff'
              }}
            >
              Take Control of Your
              <Box component="span" sx={{ 
                background: 'linear-gradient(45deg, #FFE55C, #FFB347)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'block',
                fontWeight: 800,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}>
                Financial Future
              </Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'DM Sans, IBM Plex Sans, sans-serif',
                mb: 4,
                fontWeight: 400,
                lineHeight: 1.4,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem', lg: '1.5rem' },
                letterSpacing: '-0.01em',
                textAlign: 'center',
                color: '#ffffff',
                textShadow: '0 2px 4px rgba(0,0,0,0.4)'
              }}
            >
              Altifi helps you budget smarter, save more, and invest with confidence. 
              Join thousands who've transformed their financial lives.
            </Typography>
          </Box>

          <Stack spacing={{ xs: 2, sm: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 },
                  p: { xs: 2, sm: 3 },
                  background: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(0, 0, 0, 0.4)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                  }
                }}
              >
                <Box
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '50%',
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {React.cloneElement(feature.icon, { 
                    sx: { 
                      fontSize: { xs: 32, sm: 40 }, 
                      color: '#ffffff',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                    } 
                  })}
                </Box>
                <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="h6" sx={{ 
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600, 
                    mb: 1,
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    color: '#ffffff',
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    color: 'rgba(255, 255, 255, 0.9)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          <Box sx={{ 
            mt: { xs: 4, md: 6 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 2,
            p: 2,
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <AutoGraphIcon sx={{ 
              fontSize: { xs: 24, sm: 30 }, 
              color: '#FFE55C', 
              flexShrink: 0,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
            }} />
            <Typography variant="body1" sx={{ 
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 500, 
              fontStyle: 'italic',
              letterSpacing: '0.01em',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textAlign: { xs: 'center', sm: 'left' },
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}>
              "Altifi helped me save $12,000 in my first year!" - Amanda T.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Login Form Section - Bottom on mobile, Right on desktop */}
      <Box
        sx={{
          flex: { xs: 'none', md: '0 0 35%' },
          width: { xs: '100%', md: 'auto' },
          maxWidth: { xs: 'none', sm: 500, md: 500, lg: 600 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 0,
          background: theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderLeft: { xs: 'none', md: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` },
          borderTop: { xs: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`, md: 'none' },
          position: 'relative',
          margin: { xs: 0, md: '0 0 0 auto' },
          overflow: 'visible',
          minWidth: 0,
          minHeight: { xs: 'auto', md: '100vh' },
          mx: { xs: 'auto', md: 0 }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: 400, sm: 450 },
            margin: '0 auto',
            px: { xs: 2, sm: 3, md: 4, lg: 6 },
            py: { xs: 4, sm: 6, md: 0 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <Logo 
                variant="regular" 
                width={isExtraSmall ? 120 : 150} 
                height={isExtraSmall ? 42 : 52} 
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 600,
                mb: 1,
                color: theme.palette.text.primary,
                letterSpacing: '-0.01em',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Welcome Back!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: theme.palette.text.secondary,
                mb: 2,
                fontWeight: 400,
                letterSpacing: '0.01em',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Sign in to continue your financial journey
        </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
          {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
              {error}
            </Alert>
          )}
          
          {/* Google Sign-In Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            sx={{ 
                mb: 3,
                py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 500,
              backgroundColor: 'white',
                borderColor: '#dadce0',
              color: '#3c4043',
                borderRadius: 2,
              '&:hover': {
                backgroundColor: '#f8f9fa',
                  borderColor: '#dadce0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }
            }}
          >
            Continue with Google
          </Button>

            <Divider sx={{ my: 3 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  px: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                or sign in with email
            </Typography>
          </Divider>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={handleChange}
            disabled={isLoading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:-webkit-autofill': {
                  '-webkit-box-shadow': (theme) => 
                    theme.palette.mode === 'dark' 
                      ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                      : '0 0 0 100px #fff inset',
                  '-webkit-text-fill-color': (theme) =>
                    theme.palette.mode === 'dark' ? '#fff' : '#000'
                },
                '& input:-webkit-autofill': {
                  '-webkit-box-shadow': (theme) => 
                    theme.palette.mode === 'dark' 
                      ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                      : '0 0 0 100px #fff inset',
                  '-webkit-text-fill-color': (theme) =>
                    theme.palette.mode === 'dark' ? '#fff' : '#000'
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              },
              '& .MuiOutlinedInput-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1, sm: 1.5 }
              }
            }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={handleChange}
            disabled={isLoading}
            inputProps={{ 'data-testid': 'password-input' }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:-webkit-autofill': {
                  '-webkit-box-shadow': (theme) => 
                    theme.palette.mode === 'dark' 
                      ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                      : '0 0 0 100px #fff inset',
                  '-webkit-text-fill-color': (theme) =>
                    theme.palette.mode === 'dark' ? '#fff' : '#000'
                },
                '& input:-webkit-autofill': {
                  '-webkit-box-shadow': (theme) => 
                    theme.palette.mode === 'dark' 
                      ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                      : '0 0 0 100px #fff inset',
                  '-webkit-text-fill-color': (theme) =>
                    theme.palette.mode === 'dark' ? '#fff' : '#000'
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              },
              '& .MuiOutlinedInput-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1, sm: 1.5 }
              }
            }}
          />
            
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
              sx={{ 
                py: { xs: 1.25, sm: 1.5 },
                mb: 3,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                borderRadius: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  boxShadow: '0 4px 20px rgba(124, 139, 111, 0.3)',
                }
              }}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                variant="body2"
                sx={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot your password?
              </Link>
            </Box>

            <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Don't have an account?
              </Typography>
              <Link 
                component={RouterLink} 
                to="/register" 
                variant="body2"
                sx={{ 
                  color: theme.palette.secondary.main,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Create your free account →
            </Link>

            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: 3,
                textAlign: 'center',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                lineHeight: 1.4
              }}
            >
              By using altifi, you agree to our{' '}
              <Link 
                component={RouterLink} 
                to="/terms"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                component={RouterLink} 
                to="/privacy"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Privacy Policy
              </Link>
              .
            </Typography>
            </Box>

            {/* Development Login Bypass - Only shows in development */}
            <DevLoginBypass />
          </Box>
        </Box>
      </Box>
    </Box>
    </>
  );
};

export default Login; 